/**
 * Luxury reveal — slow, subtle entrance animations.
 *
 * Design constraints:
 *  - Content is visible by default; the class that hides it is only applied
 *    once we know JS + IntersectionObserver are available (progressive
 *    enhancement, no flash of missing content).
 *  - Honours prefers-reduced-motion at runtime, including live changes.
 *  - Elements reveal once and are then unobserved.
 *  - Works with Shopify's section rendering / theme editor by re-scanning
 *    on `shopify:section:load`.
 */
(function () {
  'use strict';

  var SELECTOR = '.lux-reveal';
  var READY_CLASS = 'lux-motion-ready';
  var REVEALED_CLASS = 'is-revealed';

  var reduceMotion =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  function motionDisabled() {
    return !!(reduceMotion && reduceMotion.matches);
  }

  if (!('IntersectionObserver' in window)) return;

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(REVEALED_CLASS);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  /**
   * Stagger children of a common parent so groups cascade rather than
   * all appearing at once. Capped so long grids never feel slow.
   */
  function applyStagger(elements) {
    var groups = new Map();

    elements.forEach(function (el) {
      var parent = el.parentElement || document.body;
      if (!groups.has(parent)) groups.set(parent, []);
      groups.get(parent).push(el);
    });

    groups.forEach(function (items) {
      items.forEach(function (el, index) {
        if (el.style.getPropertyValue('--lux-reveal-delay')) return;
        var delay = Math.min(index, 5) * 90;
        if (delay > 0) el.style.setProperty('--lux-reveal-delay', delay + 'ms');
      });
    });
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var elements = Array.prototype.slice.call(scope.querySelectorAll(SELECTOR));
    if (!elements.length) return;

    if (motionDisabled()) {
      elements.forEach(function (el) {
        el.classList.add(REVEALED_CLASS);
      });
      return;
    }

    applyStagger(elements);

    elements.forEach(function (el) {
      if (el.classList.contains(REVEALED_CLASS)) return;
      observer.observe(el);
    });
  }

  function init() {
    if (!motionDisabled()) {
      document.documentElement.classList.add(READY_CLASS);
    }
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor: sections are injected after load.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  // Respect a live change to the OS motion preference.
  if (reduceMotion && typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', function () {
      if (motionDisabled()) {
        document.documentElement.classList.remove(READY_CLASS);
        document.querySelectorAll(SELECTOR).forEach(function (el) {
          el.classList.add(REVEALED_CLASS);
        });
      } else {
        document.documentElement.classList.add(READY_CLASS);
        scan(document);
      }
    });
  }
})();
